import { useState, useMemo, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Trash2, MessageSquare, ExternalLink } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/useToast';
import { Link } from '@tanstack/react-router';
import type { AccountCallout, CalloutType, CampaignData } from '@/lib/types';
import { useDeleteCalloutMutationOptions, useUpdateCalloutMutationOptions } from '@/lib/query-options';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { DataTable } from '@/components/glitch-ui/data-table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { CharacterCounter } from '@/components/ui/character-counter';

interface CalloutsSectionProps {
  campaign: CampaignData;
  disabled?: boolean;
}

// #5851: case- and whitespace-insensitive text key for duplicate detection.
const normalizeCalloutText = (text: string) => text.toLowerCase().replace(/\s+/g, ' ').trim();

type CalloutRow = {
  key: string;
  text: string;
  source: 'account' | 'campaign';
  original_account?: AccountCallout;
  original_campaign?: CalloutType;
  campaignIndex?: number;
};

export interface CalloutSectionRef {
  openAdd: () => void;
}

export const CalloutSection = forwardRef<CalloutSectionRef, CalloutsSectionProps>(({ campaign, disabled = false }, ref) => {
  const { account_callouts = [], id: campaignId } = campaign;
  const refetchCampaign = useRefetchCampaign(campaign.slug);

  const { toast } = useToast();
  const [deletingCallout, setDeletingCallout] = useState<CalloutType | null>(null);

  // Add modal state (kept for adding new callouts)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');
  // Tell-on-click (#5857): Save stays enabled; a click with an empty field marks it.
  const [modalError, setModalError] = useState('');

  const calloutDeleteMutation = useDeleteCalloutMutationOptions();
  const calloutUpdateMutation = useUpdateCalloutMutationOptions();
  const isSavingRef = useRef(false);

  const [localCallouts, setLocalCallouts] = useState<CalloutType[]>(campaign.callouts || []);
  // Sync from campaign when it changes externally (e.g. after add/delete). Previously this used
  // an in-render `useRef` check keyed on `JSON.stringify(...map(c => c.id))`, which (a) missed
  // content-only changes and (b) didn't reliably commit under React 18 strict-mode double-render,
  // so adds/deletes required a manual refresh to show (#4456). React Query's structuralSharing
  // preserves array reference equality when contents are unchanged, so a plain reference-keyed
  // effect is enough.
  useEffect(() => {
    setLocalCallouts(campaign.callouts || []);
  }, [campaign.callouts]);
  // Google serves the most specific level only: once a campaign has callouts of its own, the
  // account-level ones stop serving on it. Inherited rows stay listed but are marked (#6353).
  const isAccountOverridden = localCallouts.length > 0;

  // #5851: normalized text of every campaign-level callout, used to block adding a duplicate that
  // would create a redundant asset. Account callouts are deliberately not included: they stop
  // serving once the campaign has its own, so re-adding one here is how you keep it (#6353).
  const existingCalloutTexts = useMemo(
    () => new Set(localCallouts.map((c) => normalizeCalloutText(c.text))),
    [localCallouts],
  );

  const openAddModal = () => {
    setModalText('');
    setModalError('');
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    const text = modalText.trim();
    // Tell-on-click (#5857): mark the empty field instead of silently refusing to save.
    if (!text) {
      setModalError('Required');
      return;
    }

    // #5851: block a duplicate before it reaches the Google Ads push, which would otherwise create a
    // second distinct callout asset with identical text.
    if (existingCalloutTexts.has(normalizeCalloutText(text))) {
      setModalError('A callout with this text already exists on this campaign.');
      return;
    }

    try {
      const callout: CalloutType = { text };
      await calloutUpdateMutation.mutateAsync({ campaignId, callout });
      setIsModalOpen(false);
      refetchCampaign();
    } catch (error) {
      console.error(error);
      toast({
        description: error instanceof Error ? error.message : 'Failed to save callout',
        variant: 'destructive',
      });
    }
  };

  const handleInlineEdit = useCallback(async (callout: CalloutType, newText: string) => {
    if (callout.text === newText || !newText.trim()) return;
    if (isSavingRef.current) return;
    // Update local state immediately so text persists
    setLocalCallouts(prev => prev.map(c => c.id === callout.id ? { ...c, text: newText.trim() } : c));
    try {
      isSavingRef.current = true;
      await calloutUpdateMutation.mutateAsync({ campaignId, callout: { ...callout, text: newText.trim() } });
    } catch (error) {
      console.error(error);
      toast({
        description: error instanceof Error ? error.message : 'Failed to save callout',
        variant: 'destructive',
      });
      // Revert on failure
      setLocalCallouts(prev => prev.map(c => c.id === callout.id ? callout : c));
    } finally {
      isSavingRef.current = false;
    }
  }, [campaignId, calloutUpdateMutation, toast]);

  const handleDeleteConfirm = async () => {
    if (!deletingCallout?.id) return;
    try {
      await calloutDeleteMutation.mutateAsync({ campaignId, calloutId: deletingCallout.id });
      setDeletingCallout(null);
      refetchCampaign();
    } catch (error) {
      console.error(error);
      toast({
        description: error instanceof Error ? error.message : 'Failed to delete callout',
        variant: 'destructive',
      });
    }
  };

  // Merge both data sources — campaign callouts first (newest at top)
  const rows: CalloutRow[] = useMemo(() => [
    ...[...localCallouts].reverse().map((c, i) => ({
      key: c.id ? String(c.id) : `new-${i}`,
      text: c.text,
      source: 'campaign' as const,
      original_campaign: c,
      campaignIndex: localCallouts.length - 1 - i,
    })),
    ...account_callouts.map(ac => ({
      key: ac.customer_asset_resource_name,
      text: ac.callout_text,
      source: 'account' as const,
      original_account: ac,
    })),
  ], [localCallouts, account_callouts]);

  const columns: ColumnDef<CalloutRow, unknown>[] = useMemo(() => [
    {
      accessorKey: 'text',
      header: 'Callout',
      meta: { className: 'px-6 w-[43%]' },
      cell: function CalloutCell({ row }: { row: { original: CalloutRow } }) {
        const callout = row.original;
        const [localVal, setLocalVal] = useState(callout.text);
        const canEdit = callout.source === 'campaign' && !disabled;
        const isOverridden = callout.source === 'account' && isAccountOverridden;

        return (
          <div className='flex items-center gap-2'>
            <MessageSquare className='h-3.5 w-3.5 text-gray-400 shrink-0' />
            {canEdit ? (
              <div className="flex items-center gap-1 flex-1">
                <input
                  type="text"
                  value={localVal}
                  onChange={(e) => setLocalVal(e.target.value)}
                  onBlur={() => {
                    if (callout.original_campaign && localVal !== callout.text) {
                      handleInlineEdit(callout.original_campaign, localVal);
                    }
                  }}
                  maxLength={25}
                  className="text-sm text-gray-900 bg-transparent border border-transparent hover:border-gray-300 focus:border-[#290BD9] focus:bg-white rounded px-2 py-1 outline-none w-full transition-colors"
                />
                <span className="text-xs text-gray-400 shrink-0">{localVal.length}/25</span>
              </div>
            ) : (
              // Mirror the editable layout (flex-1 + a char-count-sized spacer) so
              // Account/disabled rows keep the same column alignment as Campaign rows. #4210
              <div className="flex items-center gap-1 flex-1">
                <span className={`text-sm px-2 py-1 flex-1 ${isOverridden ? 'text-gray-500' : 'text-gray-900'}`}>{callout.text}</span>
                <span className="text-xs text-gray-400 shrink-0 invisible" aria-hidden="true">00/25</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'source',
      header: 'Source',
      cell: ({ row }) => (
        row.original.source === 'account' ? (
          isAccountOverridden ? (
            <span
              className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500'
              title="Not serving: this campaign's own callouts take precedence over account-level callouts."
            >
              Account, overridden
            </span>
          ) : (
            <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600'>
              Account
            </span>
          )
        ) : (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-600'>
            Campaign
          </span>
        )
      ),
    },
    {
      id: 'actions',
      header: () => null,
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.source === 'account') {
          return (
            <div className='flex justify-end'>
              <Link
                to="/assets"
                hash="google"
                className='text-sm text-[#290BD9] hover:text-[#2309C5] inline-flex items-center gap-1'
              >
                Manage
                <ExternalLink className='h-3.5 w-3.5' />
              </Link>
            </div>
          );
        }

        const callout = row.original.original_campaign;
        if (!callout) return null;
        if (disabled) return null;

        return (
          <div className='flex justify-end'>
            <button
              onClick={() => setDeletingCallout(callout)}
              className='p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer'
            >
              <Trash2 className='h-4 w-4' />
            </button>
          </div>
        );
      },
    },
  ], [disabled, handleInlineEdit, isAccountOverridden]);

  useImperativeHandle(ref, () => ({ openAdd: openAddModal }), [openAddModal]);

  return (
    <div>
      {isAccountOverridden && account_callouts.length > 0 && (
        <p className='mb-3 text-xs text-gray-500'>
          Account callouts are not serving on this campaign: Google shows the most specific level, and this campaign has its own.
        </p>
      )}
      {/* Table — read-only rows */}
      <DataTable<CalloutRow>
        data={rows}
        columns={columns}
        pageSize={999}
        emptyIcon={MessageSquare}
        emptyTitle='No Callouts'
        emptyDescription='Add short phrases to highlight key benefits of your business.'
      />

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='sm:max-w-[400px] max-h-[90vh] overflow-y-auto' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Callout</DialogTitle>
          </DialogHeader>
          <div className='space-y-4 py-2'>
            <div>
              <div className='flex items-center justify-between'>
                <Label>Callout text</Label>
                <FieldError message={modalError} />
              </div>
              <div className='relative mt-1'>
                <Input
                  value={modalText}
                  onChange={(e) => { setModalText(e.target.value); setModalError(''); }}
                  maxLength={25}
                  placeholder='e.g. Free Shipping'
                  autoFocus
                  error={!!modalError}
                  className={modalError ? 'pr-20' : 'pr-14'}
                />
                <InputAdornment error={!!modalError}>
                  <CharacterCounter current={modalText.length} max={25} variant='compact' />
                </InputAdornment>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={calloutUpdateMutation.isPending}
              className='bg-[#290BD9] hover:bg-[#2309C5] leading-none'
            >
              {calloutUpdateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConformationModal
        isOpen={!!deletingCallout}
        onClose={() => setDeletingCallout(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Callout"
        description={`Are you sure you want to delete "${deletingCallout?.text}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={calloutDeleteMutation.isPending}
      />
    </div>
  );
});
CalloutSection.displayName = 'CalloutSection';
